import {
  IHealthScribeAudioProvider,
  IAudioChunk,
} from "../../../core/ports/HealthScribe";

export class HealthScribeAudioProvider implements IHealthScribeAudioProvider {
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private audioLevelInterval: number | null = null;
  private isRecordingState = false;
  private isPausedState = false;
  private currentAudioLevel = 0;

  // PCM processing properties
  private scriptProcessor: ScriptProcessorNode | null = null;

  // Complete audio recording properties
  private completeAudioRecorder: MediaRecorder | null = null;
  private completeAudioBlob: Blob | null = null;
  private recordingStartTime: number = 0;

  // Callback properties
  public onAudioChunk: ((chunk: IAudioChunk) => void) | null = null;
  public onAudioLevel: ((level: number) => void) | null = null;
  public onError: ((error: string) => void) | null = null;

  async requestMicrophonePermission(): Promise<{
    granted: boolean;
    error?: string;
  }> {
    try {
      // Check if permissions API is available
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (permission.state === "granted") {
          return { granted: true };
        } else if (permission.state === "denied") {
          return { granted: false, error: "Microphone permission denied" };
        }
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop the test stream
      return { granted: true };
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Microphone permission error:",
        error
      );
      return {
        granted: false,
        error: `Failed to get microphone permission: ${error}`,
      };
    }
  }

  async startRecording(): Promise<{ success: boolean; error?: string }> {
    try {
      if (this.isRecordingState) {
        return { success: false, error: "Recording is already in progress" };
      }

      // Get microphone stream with AWS Transcribe Medical compatible settings
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // AWS Transcribe Medical requirement
          channelCount: 1, // Mono audio
        },
      });

      // Set up Web Audio API for raw PCM data capture (AWS Transcribe Medical compatible)
      // Create AudioContext with 16kHz sample rate (AWS requirement)
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 16000,
        latencyHint: "interactive",
      });

      // Create MediaStreamSource from microphone
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Create ScriptProcessorNode for raw PCM data capture
      const bufferSize = 4096;
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        bufferSize,
        1,
        1
      );

      // Connect audio graph: source -> scriptProcessor -> destination
      this.source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      // Handle audio processing to capture raw PCM data
      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.isRecordingState || this.isPausedState) {
          return;
        }

        const inputBuffer = event.inputBuffer;
        const channelData = inputBuffer.getChannelData(0); // Get mono channel

        // Convert Float32Array to Int16Array (16-bit PCM for AWS)
        const pcmData = new Int16Array(channelData.length);
        for (let i = 0; i < channelData.length; i++) {
          // Convert from [-1, 1] float to [-32768, 32767] int16
          const clamped = Math.max(-1, Math.min(1, channelData[i]));
          pcmData[i] = clamped * 32767;
        }

        // Create binary blob from PCM data (AWS Transcribe Medical compatible)
        const pcmBlob = new Blob([pcmData.buffer], { type: "audio/pcm" });

        if (this.onAudioChunk && pcmBlob.size > 0) {
          const chunk: IAudioChunk = {
            data: pcmBlob,
            timestamp: Date.now(),
            duration:
              (inputBuffer.length / this.audioContext!.sampleRate) * 1000, // Duration in ms
          };
          this.onAudioChunk(chunk);
        }
      };

      // Set up complete audio recording MediaRecorder
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      }

      // Create MediaRecorder for complete audio file recording
      this.completeAudioRecorder = new MediaRecorder(this.mediaStream, {
        mimeType,
        audioBitsPerSecond: 256000,
      });

      // Reset state
      this.completeAudioBlob = null;
      this.recordingStartTime = Date.now();

      // Handle data available event to get the complete audio blob
      this.completeAudioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log(
            "[HealthScribe Audio Provider] Complete audio recording finished:",
            event.data.size,
            "bytes"
          );
          this.completeAudioBlob = event.data;
        }
      };

      // Handle recording errors
      this.completeAudioRecorder.onerror = (event) => {
        console.error(
          "[HealthScribe Audio Provider] Complete audio recorder error:",
          event
        );
        if (this.onError) {
          this.onError(`Complete audio recorder error: ${event}`);
        }
      };

      // Handle recording stop event
      this.completeAudioRecorder.onstop = () => {
        console.log(
          "[HealthScribe Audio Provider] Complete audio recording stopped"
        );
      };

      // Start complete audio recording as a single continuous file
      this.completeAudioRecorder.start(); // No timeslice parameter = single continuous recording
      console.log(
        "[HealthScribe Audio Provider] Started complete audio recording with mimeType:",
        mimeType
      );
      this.isRecordingState = true;
      this.isPausedState = false;

      // Set up audio level monitoring
      this.setupAudioLevelMonitoring();

      console.log(
        "[HealthScribe Audio Provider] Recording started successfully"
      );
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Failed to start recording:",
        error
      );
      return { success: false, error: `Failed to start recording: ${error}` };
    }
  }

  async stopRecording(): Promise<{ audioBlob: Blob; duration: number }> {
    return new Promise((resolve, reject) => {
      if (!this.completeAudioRecorder || !this.isRecordingState) {
        reject(new Error("No active recording to stop"));
        return;
      }

      console.log(
        "[HealthScribe Audio Provider] Stopping complete audio recording..."
      );
      const recordingDuration = Date.now() - this.recordingStartTime;

      // Set up stop handler for complete audio recorder
      this.completeAudioRecorder.onstop = () => {
        console.log(
          "[HealthScribe Audio Provider] Complete audio recording stopped"
        );

        // Wait a moment for ondataavailable to be called
        setTimeout(() => {
          if (this.completeAudioBlob) {
            console.log(
              "[HealthScribe Audio Provider] Using complete audio blob:",
              {
                size: this.completeAudioBlob.size,
                type: this.completeAudioBlob.type,
                duration: recordingDuration,
              }
            );

            const audioBlob = this.completeAudioBlob;

            // Clean up resources
            this.cleanup();

            resolve({ audioBlob, duration: recordingDuration });
          } else {
            console.error(
              "[HealthScribe Audio Provider] No audio blob available after recording stopped"
            );
            this.cleanup();
            reject(new Error("No audio data recorded"));
          }
        }, 10); // Small delay to ensure ondataavailable is called
      };

      // Stop the complete audio recording
      this.completeAudioRecorder.stop();
    });
  }

  async pauseRecording(): Promise<void> {
    if (!this.completeAudioRecorder || !this.isRecordingState) {
      throw new Error("No active recording to pause");
    }

    if (this.isPausedState) {
      throw new Error("Recording is already paused");
    }

    // Pause both PCM processing and complete audio recording
    this.completeAudioRecorder.pause();
    this.isPausedState = true;
    console.log(
      "[HealthScribe Audio Provider] Recording paused (both PCM chunks and complete audio)"
    );
  }

  async resumeRecording(): Promise<void> {
    if (!this.completeAudioRecorder || !this.isRecordingState) {
      throw new Error("No active recording to resume");
    }

    if (!this.isPausedState) {
      throw new Error("Recording is not paused");
    }

    // Resume both PCM processing and complete audio recording
    this.completeAudioRecorder.resume();
    this.isPausedState = false;
    console.log(
      "[HealthScribe Audio Provider] Recording resumed (both PCM chunks and complete audio)"
    );
  }

  isRecording(): boolean {
    return this.isRecordingState;
  }

  isPaused(): boolean {
    return this.isPausedState;
  }

  getCurrentStream(): MediaStream | null {
    return this.mediaStream;
  }

  getAudioLevel(): number {
    return this.currentAudioLevel;
  }

  /**
   * Get the current recording duration in milliseconds
   */
  getRecordingDuration(): number {
    if (!this.isRecordingState || this.recordingStartTime === 0) {
      return 0;
    }
    return Date.now() - this.recordingStartTime;
  }

  /**
   * Get information about the current recording session
   */
  getRecordingInfo(): {
    isRecording: boolean;
    isPaused: boolean;
    duration: number;
    hasCompleteAudio: boolean;
    mimeType: string | null;
  } {
    return {
      isRecording: this.isRecordingState,
      isPaused: this.isPausedState,
      duration: this.getRecordingDuration(),
      hasCompleteAudio: this.completeAudioBlob !== null,
      mimeType: this.completeAudioRecorder?.mimeType || null,
    };
  }

  private setupAudioLevelMonitoring(): void {
    if (!this.audioContext || !this.source) {
      console.warn(
        "[HealthScribe Audio Provider] AudioContext or source not available for level monitoring"
      );
      return;
    }

    try {
      // Create analyser node and connect to existing audio graph
      this.analyser = this.audioContext.createAnalyser();

      // Configure analyser
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.minDecibels = -90;
      this.analyser.maxDecibels = -10;

      // Connect source to analyser (in addition to existing connections)
      this.source.connect(this.analyser);

      console.log(
        "[HealthScribe Audio Provider] Audio level monitoring connected to existing Web Audio graph"
      );

      // Start monitoring
      this.audioLevelInterval = window.setInterval(() => {
        if (this.analyser && this.onAudioLevel) {
          const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
          this.analyser.getByteFrequencyData(dataArray);

          // Calculate average level
          const average =
            dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          this.currentAudioLevel = average;

          this.onAudioLevel(average);
        }
      }, 100); // Update every 100ms
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Failed to setup audio level monitoring:",
        error
      );
    }
  }

  private cleanup(): void {
    // Stop audio level monitoring
    if (this.audioLevelInterval) {
      window.clearInterval(this.audioLevelInterval);
      this.audioLevelInterval = null;
    }

    // Disconnect and clean up Web Audio API nodes
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    // Stop media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clean up complete audio recorder
    if (this.completeAudioRecorder) {
      this.completeAudioRecorder.onstop = null;
      this.completeAudioRecorder.ondataavailable = null;
      this.completeAudioRecorder.onerror = null;
      this.completeAudioRecorder = null;
    }

    // Reset state
    this.mediaRecorder = null;
    this.isRecordingState = false;
    this.isPausedState = false;
    this.currentAudioLevel = 0;
    this.completeAudioBlob = null;
    this.recordingStartTime = 0;

    console.log("[HealthScribe Audio Provider] Cleanup completed");
  }
}
